import { AxiosError, AxiosInstance } from 'axios'
import moment from 'moment'

import { Account } from '@driftt/api-types'
import { createAuthedHttpClient } from '@driftt/web-auth'

import { DateRange } from 'components/DateRangePicker'
import config from 'config'
import {
  AccountActivity,
  AccountActivitySortValue,
  ActivityType,
  ContactType,
  ContactWithActivites
} from 'modules/accountActivity/domain'
import { transformFiltersToBackend } from 'utils/deserializeFilters'
import { captureException } from 'utils/errors'
import {
  ALL_ACTIVITY_TYPES,
  AttributeFilter,
  ContextFilter,
  ContextFilters,
  FilterGroup
} from 'utils/filters'
import { getTaskTypeFromSubject } from 'utils/salesforce'

const logErrorHandler = (error: AxiosError) => {
  captureException(error, {
    tags: { url: window.location.href }
  })
}

const responseErrorHandlers = [logErrorHandler]

export const AccountActivityAPI: AxiosInstance = createAuthedHttpClient({
  responseErrorHandlers,
  baseConfig: {
    baseURL: config.ACCOUNT_ACTIVITY_API
  }
})

export const AccountAPI: AxiosInstance = createAuthedHttpClient({
  responseErrorHandlers,
  baseConfig: {
    baseURL: config.ACCOUNTS_API
  }
})

export const ConversationApi: AxiosInstance = createAuthedHttpClient({
  responseErrorHandlers,
  baseConfig: {
    baseURL: config.CONVERSATION_API
  }
})

export const CommentingAPI: AxiosInstance = createAuthedHttpClient({
  responseErrorHandlers,
  baseConfig: {
    baseURL: config.COMMENTING_API
  }
})

export const ContactsApi: AxiosInstance = createAuthedHttpClient({
  responseErrorHandlers,
  baseConfig: {
    baseURL: config.CONTACTS_API
  }
})

export const CustomerApi: AxiosInstance = createAuthedHttpClient({
  baseConfig: {
    baseURL: config.CUSTOMER_API
  },
  responseErrorHandlers
})

export const MetricsApi: AxiosInstance = createAuthedHttpClient({
  baseConfig: {
    baseURL: config.METRICS_API
  }
})

export const UnifiedContactsApi: AxiosInstance = createAuthedHttpClient({
  responseErrorHandlers,
  baseConfig: {
    baseURL: config.UNIFIED_CONTACTS_API
  }
})

export const VideoApi: AxiosInstance = createAuthedHttpClient({
  responseErrorHandlers,
  baseConfig: {
    baseURL: config.VIDEO_API,
    withCredentials: true,
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken'
  }
})

export const getAccountById = (accountId: string) =>
  AccountAPI.get<Account>(`/accounts/${accountId}`)

export const fetchAttributes = async () => {
  return AccountAPI.get('/attrib')
}

export const fetchAttributeOptions = async (attributeKey: string, searchValue?: string) => {
  return AccountActivityAPI.get('/v2/accounts/attribute_options', {
    params: { attributeKey, searchValue }
  })
}

export const fetchInsightById = async (insightId: string) => {
  return AccountActivityAPI.get(`/v2/insights/${encodeURIComponent(insightId)}`)
}

export interface PagedResponse<PagedItem> {
  nextPagePath: string
  data: PagedItem[]
  totalCount?: number
}

export interface TransformedFilterGroup {
  accountOwners?: ContextFilter
  selectedActivities?: ActivityType[]
  attributeFilters?: AttributeFilter[]
  selectedContactType?: ContactType
  selectedContactRole?: ContextFilter
  targetedOnly?: boolean
  selectedPageUrls?: ContextFilters
}

export interface SerializableFilterGroupResponse {
  defaultFilterId: number
  savedFilterSets: SavedFilter[]
}

export interface SavedFilter {
  id: number
  orgId: number
  ownerId: number
  name: string
  shared: boolean
  preloaded: boolean
  body: SerializedFilter
  updatedAt: number
  hasDesktop?: boolean
  hasMobile?: boolean
  hasEmail?: boolean
}

export interface SerializedFilter {
  filterGroups: SerializableFilterGroup[]
  descending: boolean
  sortBy: string
  limit: number
}

export interface SerializableFilterGroup {
  accountOwnerFilter?: ContextFilter
  activityTypes?: ActivityType[]
  attributeFilters?: AttributeFilter[]
  contactFilterType?: ContactType
  contactRoleFilter?: ContextFilter
  targetedOnly?: boolean
  contexts?: ContextFilters
}

export interface DeserializedSavedFilter {
  id: number
  orgId: number
  ownerId: number
  name: string
  shared: boolean
  preloaded: boolean
  body: DeserializedSavedFilterBody
  updatedAt: number
  hasDesktop?: boolean
  hasMobile?: boolean
  hasEmail?: boolean
}

export interface DeserializedSavedFilterBody {
  filterGroups: FilterGroup[]
  descending?: boolean
  limit?: number
  sortBy?: string
}

export const fetchPagedData = <T>({
  url,
  sortBy,
  descending,
  accountName,
  dateRange,
  accountId,
  contactId,
  filterGroups,
  insightTypes
}: {
  url: string
  sortBy?: AccountActivitySortValue
  descending?: boolean
  accountName?: string
  dateRange?: DateRange
  accountId?: string
  contactId?: string
  filterGroups: SerializableFilterGroup[]
  insightTypes?: string[]
}) =>
  AccountActivityAPI.post<PagedResponse<T>>(url, {
    sortBy,
    descending,
    accountName,
    fromTime: dateRange?.startDate.valueOf(),
    toTime: dateRange?.endDate.valueOf(),
    accountId,
    contactId,
    filterGroups,
    insightTypes
  }).then(res => res.data)

export const fetchAccountContactActivity = ({
  accountId,
  sortBy,
  selectedActivities,
  descending,
  selectedContactType
}: {
  accountId: string
  sortBy?: AccountActivitySortValue
  descending?: boolean
  selectedActivities: ActivityType[]
  selectedContactType?: ContactType
}) =>
  AccountActivityAPI.post<ContactWithActivites[]>(`v2/accounts/${accountId}/contacts`, {
    sortBy,
    activityTypes: [...selectedActivities],
    descending,
    contactFilterType: selectedContactType
  })

export const getAccountExplain = ({
  accountId,
  limit = 3
}: {
  accountId: string
  limit?: number
}) =>
  AccountActivityAPI.post<AccountActivity>(
    `v2/accounts/${accountId}/explain?topActivityCount=${limit}`,
    {
      activityTypes: [...ALL_ACTIVITY_TYPES]
    }
  )

export const getSavedFilters = async (): Promise<SerializableFilterGroupResponse> => {
  const res = await AccountActivityAPI.get<SerializableFilterGroupResponse>(`v2/filter_sets`)
  return res.data
}

const serializeSavedFilter = (savedFilter: DeserializedSavedFilter | INewSavedFilter) => {
  return {
    ...savedFilter,
    body: {
      ...savedFilter.body,
      filterGroups: transformFiltersToBackend(savedFilter.body.filterGroups)
    }
  }
}

export interface INewSavedFilter {
  orgId: number
  ownerId: number
  name: string
  shared: boolean
  preloaded: boolean
  body: any
  updatedAt: number
}

export const createSavedFilter = (newSavedFilter: INewSavedFilter) => {
  const serializedSavedFilter = serializeSavedFilter(newSavedFilter)
  return AccountActivityAPI.post('/v2/filter_sets', serializedSavedFilter)
}

export const patchSavedFilter = (
  savedFilterId: number,
  updatedSavedFilter: DeserializedSavedFilter
) => {
  const serializedSavedFilter = serializeSavedFilter(updatedSavedFilter)
  return AccountActivityAPI.post(`/v2/filter_sets/${savedFilterId}`, serializedSavedFilter)
}

export const deleteSavedFilter = (savedFilterId: number) => {
  return AccountActivityAPI.delete(`/v2/filter_sets/${savedFilterId}`)
}

export const setDefaultFilter = (savedFilterId: number) => {
  return AccountActivityAPI.post(`/v2/filter_sets/default/${savedFilterId}`)
}

export const toggleDefaultFilter = (savedFilterId: number, isDefault: boolean) => {
  const deselectedParam = !isDefault ? '?deselected=true' : ''
  return AccountActivityAPI.post(`/v2/filter_sets/default/${savedFilterId}${deselectedParam}`)
}

export const toggleUserProperty = async (
  userId: number,
  property: string,
  value: boolean
): Promise<void> => {
  const operation = [
    {
      property,
      operation: 'set',
      value
    }
  ]
  const resp = await CustomerApi.patch(`/users/${userId}/properties2`, operation)
  return resp.data
}

export const fetchEndUserData = async (endUserId: string) => {
  return CustomerApi.get(`/end_users/${endUserId}`)
}

export const fetchSalesforceByDomain = async (domain: string) => {
  return ConversationApi.get(`/salesforce/enrichment?domain=${encodeURIComponent(domain)}`)
}

export const fetchSalesforceByAccount = async (email: string) => {
  return ConversationApi.get(`/salesforce/enrichment?email=${encodeURIComponent(email)}`)
}

export const fetchSalesforceRefreshFieldsById = async (endUserId: string, attributeDefs: any) => {
  return ConversationApi.post(
    `/salesforce/sync/sidebar/refresh?enduserid=${encodeURIComponent(endUserId)}`,
    attributeDefs
  )
}

export const getPropertyDefinitions = async () => {
  return CustomerApi.get('/property_definitions')
}

export const getDriftProperties = async () => {
  return CustomerApi.get('/contacts/attributes/retrieve/system')
}

export const getSystemProperties = async () => {
  return CustomerApi.get('/contacts/attributes/retrieve/programmatic')
}

export const getGeoIPProperties = async (endUserId: string) => {
  return ContactsApi.get(`/end_user/${endUserId}/properties/geoip`)
}

export const getDriftIntel = async (endUserId: string, targets: number[]) => {
  return ContactsApi.post('/derivation', { endUserId, targets })
}

export const fetchEndUserTimeline = (endUserId: string, { size = 10 }: Record<any, any>) => {
  return CustomerApi.get(`/end_users/${endUserId}/timeline?size=${size}&filter=CONVERSATION`)
}

export const getUnifiedContact = (endUserId: string) => {
  return UnifiedContactsApi.get(`/${endUserId}`)
}

export const findAccount = (orgId: number, domain: string) => {
  return AccountAPI.get(
    `/accounts/findAccount?orgId=${encodeURIComponent(orgId)}&domain=${encodeURIComponent(domain)}`
  )
}

export const createComment = ({
  body,
  resourceId,
  parentCommentId,
  taggedUsers
}: {
  body: string
  resourceId: string
  parentCommentId?: string
  taggedUsers?: string[]
}) => {
  return CommentingAPI.post('/comments', {
    content: body,
    resourceId,
    parentCommentId,
    taggedUsers
  })
}

export const deleteComment = (resourceId: string, commentId: string) => {
  return CommentingAPI.delete(`/comments/${resourceId}/${commentId}`)
}

export const fetchSalesforceAttributeDefs = () => {
  return CustomerApi.get('/organizations/sidebar/settings')
}

export const fetchDeals = ({
  filters,
  accountId,
  search,
  dateRange,
  sortValue,
  sortDescending
}: {
  filters: Record<string, any>
  accountId?: string
  search?: string
  dateRange?: {
    from: number
    to: number
  }
  sortValue: AccountActivitySortValue
  sortDescending: boolean
}) => {
  return AccountActivityAPI.post('/deals', {
    sortBy: sortValue,
    descending: sortDescending,
    accountId,
    accountName: search,
    fromTime: dateRange?.from,
    toTime: dateRange?.to,
    filterGroups: [filters]
  }).then(res => res.data)
}

export const fetchDealTotals = ({
  filters,
  search,
  dateRange
}: {
  filters: Record<string, any>
  search: string | undefined
  dateRange:
    | {
        from: number
        to: number
      }
    | undefined
}) => {
  return AccountActivityAPI.post('/deals/totals', {
    accountName: search,
    fromTime: dateRange?.from,
    toTime: dateRange?.to,
    filterGroups: [filters]
  }).then(res => res.data)
}

export const fetchAccountEngagementSummary = async (
  accountId: string,
  firstDayOfMonth: number,
  lastDayOfMonth: number,
  withTasks?: boolean
) => {
  const driftEngagementSummary = AccountActivityAPI.get(
    `/v2/accounts/${accountId}/summary?startTime=${firstDayOfMonth}&endTime=${lastDayOfMonth}`
  ).then(res => res.data)

  if (withTasks) {
    try {
      const tasks = await fetchSalesforceTasks({
        accountId,
        startDate: moment(firstDayOfMonth).format('YYYY-MM-DD'),
        endDate: moment(lastDayOfMonth).format('YYYY-MM-DD')
      })

      const driftData = await driftEngagementSummary
      const combinedEngagementData = tasks.reduce((acc: any, currentVal: any, idx: number) => {
        const type = getTaskTypeFromSubject(currentVal.Subject) || currentVal.Type
        const day = moment(currentVal.ActivityDate)
          .toDate()
          .getTime()
        const engagementByDay = acc.engagementByDay
        const dataByDate = engagementByDay.find(({ startOfDay }: any) => startOfDay === day) || {}
        let count = dataByDate?.engagementByType[type] || 0

        engagementByDay.indexOf(dataByDate) > -1 &&
          (engagementByDay[engagementByDay.indexOf(dataByDate)] = {
            ...dataByDate,
            engagementByType: {
              ...dataByDate.engagementByType,
              [type]: ++count
            }
          })

        return {
          ...acc,
          engagementByDay: engagementByDay
        }
      }, driftData)

      return Promise.resolve(combinedEngagementData)
    } catch (e) {
      return Promise.reject(e)
    }
  } else {
    return driftEngagementSummary
  }
}

export const fetchVideoDetails = (videoId: number) => {
  return VideoApi.get(`/apis/v1/player/videos/${videoId}/`).then(res => res.data)
}

export const fetchMeetingInsights = (videoId: number) => {
  return AccountActivityAPI.get(`/conversation/insights/meeting/${videoId}`).then(res => res.data)
}

export const fetchMissedAccountsCSV = (orgId: number) => {
  return AccountActivityAPI.get(`/v2/activity_report/missed_accounts/org/${orgId}/export`).then(
    res => res.data
  )
}

export const fetchSalesforceTasks = ({
  accountId,
  ...params
}: {
  accountId: string
  type?: string
  startDate?: string
  endDate?: string
}) => {
  let query: URLSearchParams | string = new URLSearchParams({ ...params })
  query = query ? `?${query}` : ''
  return AccountActivityAPI.get(`/tasks/sfdc/fetch/${accountId}${query}`).then(({ data }) => data)
}
